import { useMemo } from 'react';
import { isEmpty /*, isNil*/ } from 'lodash-es';
import { GameRoundData } from '../@types/game';
/*import {
  getRoundDataFromBlockchainResponse,
  getBlockchainCorrespondingToken,
} from '../utils/blockchain-helpers.ts';*/
import {
  useGameRoundWebsocketQuery,
  useCurrentGameRoundDataBlockchainQuery,
} from '../services/queries';
import { useCurrencyStore } from '../services/store';
import { useIsBlockchainVersion } from './useIsBlockchainVersion.ts';

export const useGameRoundData = () => {
  const blockchainVersion = useIsBlockchainVersion();
  const currencyId = useCurrencyStore(({ currency }) => currency.id);

  /*
   * Because new currency id 100 has been added
   * this trick is required. Should be properly mapped in the future
   * */
  const multiplierId = currencyId !== 100 ? currencyId : 1;

  const { data: websocketData, ...restWebsocketResponse } =
    useGameRoundWebsocketQuery();

  const { data: blockchainData, ...restBlockchainResponse } =
    useCurrentGameRoundDataBlockchainQuery({
      enabled: blockchainVersion,
    });

  const data = useMemo<GameRoundData | undefined>(() => {
    if (!websocketData) return;

    if (!blockchainVersion) {
      return {
        ...websocketData,
        mul: websocketData.mul[multiplierId],
      };
    }

    if (isEmpty(blockchainData)) return;

    const [currentRoundNumber] = blockchainData!;

    /*const [currentRoundNumber, currentRoundData] = blockchainData!;

    if (!(!isNil(currentRoundNumber) && !isEmpty(currentRoundData))) return;

    const blockchainAdaptedData = getRoundDataFromBlockchainResponse(
      currentRoundData!
    );

    const mul: GameRoundData['mul'] = {
      a: blockchainAdaptedData.betAmounts
        .filter((value) => value !== 0n)
        .reduce(
          (acc, value, index) => {
            return {
              ...acc,
              [getBlockchainCorrespondingToken(index)]: Number(value),
            };
          },
          {} as GameRoundData['mul']['a']
        ),
      sumA: Number(blockchainAdaptedData.totalBetsAmount),
      b: blockchainAdaptedData.groupBetAmounts
        .filter((value) => value !== 0n)
        .reduce(
          (acc, value, index) => {
            return {
              ...acc,
              [index === 0 ? 'A' : 'B']: Number(value),
            };
          },
          {} as GameRoundData['mul']['b']
        ),
      sumB: Number(blockchainAdaptedData.totalGroupBetsAmount),
    };*/

    return {
      round: Number(currentRoundNumber),
      status: websocketData.status,
      startAt: websocketData.startAt,
      endAt: websocketData.endAt,
      timeRemaining: websocketData.timeRemaining,
      position: websocketData.position,
      growth: websocketData.growth,
      mul: websocketData.mul[multiplierId],
      jackpotEvent: websocketData.jackpotEvent,
    };
  }, [multiplierId, blockchainVersion, websocketData, blockchainData]);

  return useMemo(() => {
    if (!blockchainVersion) {
      return {
        data,
        ...restWebsocketResponse,
      };
    }

    return {
      ...restBlockchainResponse,
      data,
      // Loading states
      isPending:
        restBlockchainResponse.isPending || restWebsocketResponse.isPending,
      isLoading:
        restBlockchainResponse.isLoading || restWebsocketResponse.isLoading,
      isFetching:
        restBlockchainResponse.isFetching || restWebsocketResponse.isFetching,

      // Error states - if at least one source has an error
      isError: restBlockchainResponse.isError || restWebsocketResponse.isError,
      error: restBlockchainResponse.error || restWebsocketResponse.error,

      // Success state - only if both sources are successful
      isSuccess:
        restBlockchainResponse.isSuccess && restWebsocketResponse.isSuccess,

      // Status - shows the worst status
      status:
        restBlockchainResponse.isError || restWebsocketResponse.isError
          ? 'error'
          : restBlockchainResponse.isPending || restWebsocketResponse.isPending
            ? 'pending'
            : 'success',
    };
  }, [blockchainVersion, restBlockchainResponse, data, restWebsocketResponse]);
};
